import { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png';
import {
    getStaffPlanEstimates,
    createStaffPlanEstimate,
    updateStaffPlanEstimate
} from "../../services/AppService";
import { useNavigate } from "react-router-dom";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import AddStaffPlanEstimateForm from "../../components/dashboard/Prceedings/PlanEstimates/AddStaffPlanEstimateForm";
import ViewPlanEstimateDetailsModal from "../../components/dashboard/Prceedings/PlanEstimates/ViewPlanEstimateDetailsModal";
import { formatCurrency } from "../../utility/formatCurrency";
import { useTranslation } from "react-i18next";

const PlanEstimates = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [estimates, setEstimates] = useState([]);
    const [editingEstimate, setEditingEstimate] = useState(null);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedEstimate, setSelectedEstimate] = useState(null);

    // Pagination & Search State
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const [confirmModal, setConfirmModal] = useState({ open: false, formData: null });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const showSuccess = (msg) => setMessageModal({ open: true, type: "success", title: "Success", message: msg });
    const showError = (msg) => setMessageModal({ open: true, type: "error", title: "Error", message: msg });

    // Handle Search Debouncing
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchEstimates();
    }, [page, size, debouncedSearch]);

    const fetchEstimates = async () => {
        try {
            setLoading(true);
            const response = await getStaffPlanEstimates(debouncedSearch, page, size);
            if (response) {
                setEstimates(response.content || []);
                setTotalPages(response.totalPages || 0);
            }
        } catch (error) {
            showError("Failed to load plan estimates");
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({ open: true, formData: formData });
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            if (editingEstimate) {
                await updateStaffPlanEstimate(editingEstimate.id, formData);
                showSuccess("Plan Estimate updated successfully");
            } else {
                await createStaffPlanEstimate(formData);
                showSuccess("Plan Estimate added successfully");
            }
            fetchEstimates();
            setConfirmModal({ open: false, formData: null });
            setEditingEstimate(null);
        } catch (error) {
            showError(error.response?.data?.message || "Internal Server Error");
        }
    };

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t('pageTitles.planEstimate')}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={() => { setEditingEstimate(null); setIsModalOpen(true); }}>
                    {t('common.addNew')}
                </button>
            </div>

            {/* Entries and Search Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t('common.show')}</label>
                    <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2 py-1 text-sm">
                        {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label className="text-sm">{t('common.entries')}</label>
                </div>
                <input
                    type="text"
                    placeholder={t('common.search')}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    className="border rounded px-3 py-1 text-sm"
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border text-sm ctable rounded-lg">
                    <thead>
                        <tr className="bg-[#a0adf1] text-white">
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.workName')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.location')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.workType')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.estimateCost')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.date')}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.view')}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t('tables.edit')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center py-4">{t('common.loading')}</td></tr>
                        ) : estimates.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-4">{t('common.noRecords')}</td></tr>
                        ) : (
                            estimates.map((item, idx) => (
                                <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                                    <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                    <td className="px-3 py-2">{item.nameOfWork || ' '}</td>
                                    <td className="px-3 py-2">{item.districtName || ' '}</td>
                                    <td className="px-3 py-2">{item.workTypeName || ' '}</td>
                                    <td className="px-3 py-2">{formatCurrency(item.estimateCost || ' ')}</td>
                                    <td className="px-3 py-2">{item.date || ' '}</td>
                                    <td className="px-3 py-2 text-center">
                                        <button className="px-3 btnImgView py-1 rounded text-xs" onClick={() => { setSelectedEstimate(item); setViewModalOpen(true); }}></button>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <button className="px-3 py-1 btnImgEdit rounded text-xs" onClick={() => { setEditingEstimate(item); setIsModalOpen(true); }}></button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-4 text-sm">
                <p>
                    {t('common.showing')} {page * size + 1} {t('common.to')} {page * size + estimates.length} {t('common.of')} {totalPages * size} {t('common.entries')}
                </p>
                <div className="flex gap-2">
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>
                        {t('common.previous')}
                    </button>
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page === totalPages - 1}>
                        {t('common.next')}
                    </button>
                </div>
            </div>
            {/* </div> */}

            <AddStaffPlanEstimateForm
                onSubmit={handleFormSubmit}
                editData={editingEstimate}
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingEstimate(null); }}
            />

            <ConfirmActionModal
                open={confirmModal.open}
                title={editingEstimate ? "Confirm Update" : "Confirm Addition"}
                message={`Are you sure you want to ${editingEstimate ? "update" : "save"} this plan estimate?`}
                onConfirm={handleConfirmSave}
                onClose={() => setConfirmModal({ open: false, formData: null })}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />

            <ViewPlanEstimateDetailsModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                plan={selectedEstimate}
            />
        </div>
    );
};

export default PlanEstimates;